import React, { useEffect, useRef } from 'react';
import './AudioVisualizer.scss';

const variants = {
  BARS: 'bars',
  WAVEFORM: 'waveform',
  DOTS: 'dots',
  RADIAL: 'radial',
  AREA: 'area',
  SPIRAL: 'spiral',
  MIRRORED_WAVEFORM: 'mirrored_waveform',
};

const defaultColors = {
  primary: '#00b3b8',
  secondary: '#ff69b4',
  shadow: '#33243f',
  gradientHue: true,
};

const FFTSIZE = {
  BARS: 4096,
  WAVEFORM: 512,
  DOTS: 1024,
  RADIAL: 2048,
  AREA: 512,
  SPIRAL: 512,
  MIRRORED_WAVEFORM: 512,
};

export default function AudioVisualizer({
  stream,
  variant = variants.RADIAL,
  theme = 'light',
  colors = {},
}) {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  const colorScheme = { ...defaultColors, ...colors };

  useEffect(() => {
    if (!stream) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio || 1;
    const cssWidth = canvas.offsetWidth;
    const cssHeight = canvas.offsetHeight;
    canvas.width = cssWidth * pixelRatio;
    canvas.height = cssHeight * pixelRatio;
    ctx.scale(pixelRatio, pixelRatio);
    const width = cssWidth;
    const height = cssHeight;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    analyser.fftSize = FFTSIZE[variant] || FFTSIZE.BARS;
    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    function drawBars() {
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, width, height);
      const barWidth = (width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255.0) * height;
        const hue = (i / bufferLength) * 360;
        const gradient = ctx.createLinearGradient(
          0,
          height,
          0,
          height - barHeight
        );

        if (colorScheme.gradientHue) {
          gradient.addColorStop(0, `hsl(${hue}, 100%, 50%)`);
          gradient.addColorStop(1, `hsl(${hue}, 100%, 70%)`);
          ctx.shadowColor = `hsl(${hue}, 100%, 50%)`;
        } else {
          gradient.addColorStop(0, colorScheme.primary);
          gradient.addColorStop(
            1,
            colorScheme.secondary || colorScheme.primary
          );
          ctx.shadowColor = colorScheme.shadow || colorScheme.primary;
        }

        ctx.fillStyle = gradient;
        ctx.shadowBlur = theme === 'light' ? 3 : 10;
        ctx.fillRect(x, height - barHeight, barWidth, barHeight);
        x += barWidth + 1;
      }
    }

    function drawWaveform() {
      analyser.getByteTimeDomainData(dataArray);
      ctx.clearRect(0, 0, width, height);

      ctx.lineWidth = 2;
      ctx.strokeStyle = colorScheme.gradientHue
        ? 'rgba(255, 105, 180, 0.9)'
        : colorScheme.primary;
      ctx.shadowColor = colorScheme.shadow || colorScheme.primary;
      ctx.shadowBlur = 12;

      const midY = height / 2;
      const sliceWidth = width / bufferLength;
      const amplitudeMultiplier = 1.5;

      let x = 0;
      let prevY = 0;

      ctx.beginPath();
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v - 1) * midY * amplitudeMultiplier + midY;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          const cpX = x - sliceWidth / 2;
          const cpY = prevY;
          ctx.quadraticCurveTo(cpX, cpY, x, y);
        }

        prevY = y;
        x += sliceWidth;
      }

      ctx.stroke();
    }

    function drawDots() {
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, width, height);
      const numCols = 40;
      const numRows = 20;
      const spacingX = width / numCols;
      const spacingY = height / numRows;
      let i = 0;
      for (let row = 0; row < numRows; row++) {
        for (let col = 0; col < numCols; col++) {
          const amplitude = dataArray[i % bufferLength];
          const radius = (amplitude / 255) * 8 + 1;
          const x = col * spacingX + spacingX / 2;
          const y = row * spacingY + spacingY / 2;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, 2 * Math.PI);
          ctx.fillStyle = colorScheme.gradientHue
            ? `hsla(${(i / bufferLength) * 360}, 100%, 70%, 0.8)`
            : colorScheme.primary;
          ctx.shadowColor = colorScheme.shadow;
          ctx.shadowBlur = 8;
          ctx.fill();
          i++;
        }
      }
    }

    function drawRadial() {
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, width, height);
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) * 0.25;
      const bars = 120;
      const step = (Math.PI * 2) / bars;
      for (let i = 0; i < bars; i++) {
        const value = dataArray[i % bufferLength] / 255.0;
        const barLength = value * 60 + 10;
        const angle = step * i;
        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;
        const x2 = centerX + Math.cos(angle) * (radius + barLength);
        const y2 = centerY + Math.sin(angle) * (radius + barLength);
        const hue = (i / bars) * 360;
        ctx.strokeStyle = colorScheme.gradientHue
          ? `hsl(${hue}, 100%, 60%)`
          : colorScheme.primary;
        ctx.lineWidth = 2;
        ctx.shadowColor = colorScheme.shadow || colorScheme.primary;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }

    function drawAreaWaveform() {
      analyser.getByteTimeDomainData(dataArray);
      ctx.clearRect(0, 0, width, height);

      const midY = height / 2;
      const sliceWidth = width / bufferLength;
      const amplitudeMultiplier = 1.5;

      ctx.beginPath();
      ctx.moveTo(0, midY);

      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const value = dataArray[i] / 255.0;
        const y = value * height * amplitudeMultiplier;
        ctx.lineTo(x, y);
        x += sliceWidth;
      }

      ctx.lineTo(width, midY);
      ctx.closePath();

      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, colorScheme.primary);
      gradient.addColorStop(1, colorScheme.secondary || colorScheme.primary);

      ctx.fillStyle = gradient;
      ctx.shadowColor = colorScheme.shadow || colorScheme.primary;
      ctx.shadowBlur = theme === 'light' ? 3 : 10;
      ctx.fill();
    }

    function drawSpiral() {
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, width, height);
      const centerX = width / 2;
      const centerY = height / 2;
      const spiralTurns = 4;
      const points = 360;
      const maxRadius = Math.min(width, height) * 0.45;
      const angleStep = (Math.PI * 2 * spiralTurns) / points;
      ctx.lineWidth = 2;
      for (let i = 0; i < points; i++) {
        const value = dataArray[i % bufferLength];
        const normalized = value / 255;
        const angle = i * angleStep;
        const radius = (i / points) * maxRadius;
        const barLength = normalized * 25 + 5;
        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;
        const x2 = centerX + Math.cos(angle) * (radius + barLength);
        const y2 = centerY + Math.sin(angle) * (radius + barLength);
        const hue = (i / points) * 360;
        ctx.strokeStyle = colorScheme.gradientHue
          ? `hsl(${hue}, 100%, 60%)`
          : colorScheme.primary;
        ctx.shadowColor = colorScheme.shadow || colorScheme.primary;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }

    function drawMirroredWaveform() {
      analyser.getByteTimeDomainData(dataArray);
      ctx.clearRect(0, 0, width, height);
      const midY = height / 2;
      const sliceWidth = width / bufferLength;
      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = colorScheme.primary;
      ctx.shadowColor = colorScheme.shadow;
      ctx.shadowBlur = 10;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * midY;
        if (i === 0) ctx.moveTo(x, midY - y);
        else ctx.lineTo(x, midY - y);
        x += sliceWidth;
      }
      ctx.stroke();
      ctx.beginPath();
      x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * midY;
        if (i === 0) ctx.moveTo(x, midY + y);
        else ctx.lineTo(x, midY + y);
        x += sliceWidth;
      }
      ctx.stroke();
    }

    function animate() {
      animationRef.current = requestAnimationFrame(animate);
      if (variant === variants.BARS) drawBars();
      else if (variant === variants.WAVEFORM) drawWaveform();
      else if (variant === variants.DOTS) drawDots();
      else if (variant === variants.RADIAL) drawRadial();
      else if (variant === variants.AREA) drawAreaWaveform();
      else if (variant === variants.SPIRAL) drawSpiral();
      else if (variant === variants.MIRRORED_WAVEFORM) drawMirroredWaveform();
    }

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (source) source.disconnect();
      if (analyser) analyser.disconnect();
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch((err) => {
          console.warn('Error closing AudioContext:', err);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, theme, variant]);

  return (
    <div className={`audio-visualizer ${theme} ${variant}`}>
      <canvas ref={canvasRef} className={variant} />
    </div>
  );
}
